import React from 'react';

import i18n from 'i18n-js';
import { Typography, IconButton } from '@material-ui/core';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import { useDispatch } from 'react-redux';

import styles from '../styles';
import { AdvancedPriceOption } from './AdvancedPriceOption';
import { PriceOptionForm } from './PriceOptionForm';
import { useAppSelector } from 'index';
import { actionCreators } from 'store/restaurantDetailsStore/actionCreators';
import agent from 'api/agent';
import globalStore from 'store/globalStore';
import { ManagedGroupPricing } from 'api/models';

export interface PricingLevelAccessState {
    isGenerated: boolean;
    accessCode: string;
    generatedAt: string | null;
    registrationUrl: string;
    qrCodeUrl: string;
}

interface PricingLevelsCardProps {
    group?: ManagedGroupPricing;
}

export function PricingLevelsCard({ group }: PricingLevelsCardProps) {
    const classes = styles();
    const dispatch = useDispatch();
    const restaurant = useAppSelector((state) => state.restaurantDetails.restaurantInformation);
    const [addNew, setAddNew] = React.useState(false);
    const [openedAccessPanelPriceLevelId, setOpenedAccessPanelPriceLevelId] =
        React.useState<string | null>(null);

    const levels = group ? group.pricingLevels : restaurant.pricingLevels;

    const pricingLevelAccessStateById = React.useMemo(() => {
        const stateById: Record<string, PricingLevelAccessState> = {};

        levels.forEach((level) => {
            if (level.accessCode && level.qrCodeUrl) {
                stateById[level.id] = {
                    isGenerated: true,
                    accessCode: level.accessCode,
                    generatedAt: level.accessCodeGeneratedAt ?? null,
                    registrationUrl: level.qrCodeUrl,
                    qrCodeUrl: level.qrCodeUrl,
                };
            }
        });

        return stateById;
    }, [levels]);

    function getQrCanvasId(priceLevelId: string) {
        return `pricing-level-qr-canvas-${priceLevelId}`;
    }

    function createNewPriceOption(name: string) {
        setAddNew(false);
        dispatch(
            group
                ? actionCreators.createGroupPricingLevel(restaurant.id, group.groupId, name)
                : actionCreators.createPricingLevel(restaurant.id, name)
        );
    }

    function toggleAccessPanel(priceLevelId: string) {
        setOpenedAccessPanelPriceLevelId((currentId) =>
            currentId === priceLevelId ? null : priceLevelId
        );
    }

    function generateForPriceLevel(priceLevelId: string) {
        dispatch(
            group
                ? actionCreators.createGroupPricingLevelAccessCode(
                      restaurant.id,
                      group.groupId,
                      priceLevelId
                  )
                : actionCreators.createPricingLevelAccessCode(restaurant.id, priceLevelId)
        );
    }

    async function downloadQrCode(priceLevelId: string, levelName: string) {
        dispatch(globalStore.actionCreators.showSpiner());
        try {
            const fileName = `pricing-level-${levelName}`;
            await (group
                ? agent.Restaurants.GenerateGroupPricingLevelAccessCodePDF(
                      restaurant.id,
                      group.groupId,
                      priceLevelId,
                      fileName
                  )
                : agent.Restaurants.GeneratePricingLevelAccessCodePDF(
                      restaurant.id,
                      priceLevelId,
                      fileName
                  ));
        } catch (error) {
            globalStore.actionCreators.showToaster('error', error)(dispatch);
        } finally {
            dispatch(globalStore.actionCreators.hideSpiner());
        }
    }

    return (
        <div className={classes.pricesContainer}>
            <Typography variant="h6" align="left">
                {group ? group.groupName : i18n.t('restaurant.pricingLevels')}
            </Typography>
            {group ? (
                <Typography className={classes.sectionDescription}>
                    {i18n.t('restaurant.groupPricingLevelsCopy', { groupName: group.groupName })}
                </Typography>
            ) : (
                <AdvancedPriceOption name={i18n.t('common.standard')} disabled />
            )}
            {levels.map((level) => (
                <AdvancedPriceOption
                    key={level.id}
                    name={level.name}
                    id={level.id}
                    group={group}
                    accessState={pricingLevelAccessStateById[level.id]}
                    isAccessPanelOpen={openedAccessPanelPriceLevelId === level.id}
                    qrCanvasId={getQrCanvasId(level.id)}
                    onAccessPanelToggle={toggleAccessPanel}
                    onGenerateAccess={generateForPriceLevel}
                    onRegenerateAccess={generateForPriceLevel}
                    onDownloadQr={downloadQrCode}
                />
            ))}
            {addNew ? (
                <PriceOptionForm action={createNewPriceOption} cancel={() => setAddNew(false)} />
            ) : (
                <IconButton onClick={() => setAddNew(true)}>
                    <AddCircleOutlineIcon
                        color="primary"
                        fontSize="large"
                        style={{ cursor: 'pointer', marginTop: 15 }}
                    />
                </IconButton>
            )}
        </div>
    );
}
